/**
 * Onboarding — a guided flow in seven steps (w12b, C11, FR-A9, T-1.22; FR-A1).
 *
 * The owner, 2026-09-25: *"onboarding should be more atractive, in proper
 * flow"*. So: two panels — a stepper on the left with a line per step and a
 * tick for each one done, the step on the right — and on a phone *"Step 3 of
 * 7"* with a progress bar. Profession · Your firm · Address & hours · Billing
 * defaults · Website · Domain · Done.
 *
 * **Resumable, and the state is a row on the server** (FR-A1). Not
 * `localStorage`: a firm signs out on the office desktop and comes back on a
 * laptop, and the partner who started is often not the person who finishes.
 * The step shown on arrival is the service's (`furthest_step`), which reads the
 * data *and* a cursor — the cursor is what keeps the two optional steps from
 * being jumped over (`app/domain/firm.py`). A firm stopped at an old step name
 * (`details`, `template`) lands on its new equivalent.
 *
 * **Each step sends only its own fields** (`STEP_FIELDS`), and only those that
 * changed — the settings pages' rule (`changedFields`, 12A). A step can then
 * never be refused for an answer that belongs to another. *Skip for now* on an
 * optional step is the same request with nothing in it: the service moves the
 * cursor and the draft for that step goes back to what was saved.
 *
 * **The platform's organisation fills blanks only** (ADR-0029): its values are
 * put into empty fields on *Your firm* and *Address & hours*, marked *From your
 * organisation*, and saved only when the firm continues. A field the firm has
 * answered is never replaced here.
 *
 * **A domain is not required to finish.** ADR-0008 rule 4 makes the website
 * and the portal wait for a binding; clients, cases and billing have nothing
 * to do with a hostname.
 *
 * **And nothing on the Domain step is typed** (C27, UAT report #7; ADR-0051
 * §1 and its Consequences). It is the same card as Website → Domain
 * (`DomainStep`): core's bound hosts, adopted or picked. The Hostname box and
 * *Check this hostname* that stood here wrote `custom_domain` through a door
 * `PATCH /profile` had closed, so *Finish setup* now only finishes.
 */

import { Alert, Button, Skeleton } from '@fiveox/ui'
import { ArrowLeft, ArrowRight } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { SETTINGS, useEvaluation } from '../../lib/access'
import { ApiError } from '../../lib/api'
import {
  type FirmProfile,
  type FirmSettings,
  type OrgSuggestion,
  type Profession,
  STEP_LABELS,
  firm,
  refusalDetail,
} from '../../lib/api/firm'
import { site } from '../../lib/api/site'
import { DomainStep } from './DomainStep'
import { DoneStep } from './onboarding/DoneStep'
import {
  AddressStep,
  BillingStep,
  FirmStep,
  addressStepProblem,
  billingStepProblem,
  firmStepProblem,
} from './onboarding/FormSteps'
import { ProfessionCards, professionName } from './onboarding/ProfessionCards'
import { StepProgress, Stepper } from './onboarding/Stepper'
import {
  BILLING_FIELDS,
  FALLBACK_OPTIONAL,
  ONBOARDING_STEPS,
  type OnboardingRead,
  type OnboardingStep,
  STEP_FIELDS,
  canOpen,
  fillBlanks,
  nextStep,
  previousStep,
  resumeStep,
  stepGaps,
} from './onboarding/steps'
import { WebsiteStep } from './onboarding/WebsiteStep'
import { changedFields, hoursProblem } from './settings/rules'

/** What each step's heading says — the question the step asks. */
const HEADINGS: Record<OnboardingStep, { title: string; description: string }> = {
  profession: {
    title: 'What kind of practice is this?',
    description:
      'It picks your starting services and workflows, the website templates suggested first, and the conduct defaults your institute expects. Not one of the four? Choose Other and say what you do.',
  },
  firm: {
    title: 'Tell us about your firm',
    description: 'What your invoices, your website and the messages your clients receive will say.',
  },
  address: {
    title: 'Where to find you',
    description: 'Your office address and opening hours, for your invoices and your website’s footer.',
  },
  billing: {
    title: 'How your invoices are numbered',
    description: 'Three defaults every invoice uses. Each can be changed on a single invoice later.',
  },
  website: {
    title: 'Pick a look for your website',
    description:
      'Every template is drawn on sample content for your profession. Switching later keeps every word you write.',
  },
  domain: {
    title: 'Your domain',
    description:
      'Your website and client portal live on your own domain. Bind it in the platform console and ProDesk picks it up here — there is nothing to type.',
  },
  done: { title: 'All set', description: '' },
}

function pick<T extends object>(source: Partial<T>, fields: Array<keyof T>): Partial<T> {
  const out: Partial<T> = {}
  for (const field of fields) out[field] = source[field]
  return out
}

export function Onboarding() {
  const { can } = useEvaluation()
  const editable = can(SETTINGS, 'can_update')

  const [read, setRead] = useState<OnboardingRead | null>(null)
  /** The profile as the server holds it, and the one on screen. */
  const [saved, setSaved] = useState<Partial<FirmProfile>>({})
  const [draft, setDraft] = useState<Partial<FirmProfile>>({})
  const [savedSettings, setSavedSettings] = useState<Partial<FirmSettings>>({})
  const [settings, setSettings] = useState<Partial<FirmSettings>>({})
  const [prefilled, setPrefilled] = useState<Array<keyof FirmProfile>>([])
  const [viewing, setViewing] = useState<OnboardingStep>('profession')
  const [templateName, setTemplateName] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loadFailed, setLoadFailed] = useState(false)
  const [saving, setSaving] = useState(false)

  const accept = useCallback((body: OnboardingRead) => {
    setRead(body)
    setSaved(body.profile ?? {})
    setSavedSettings(body.settings ?? {})
  }, [])

  const load = useCallback(async () => {
    setLoadFailed(false)
    const [body, suggestions] = await Promise.all([
      firm.onboarding(),
      // ADR-0029 §3: no suggestion is never an error in onboarding.
      firm
        .orgSuggestions()
        .then((r) => r.data.fields)
        .catch((): OrgSuggestion[] => []),
    ])
    const data = body.data as OnboardingRead
    accept(data)
    const profile = data.profile ?? {}
    const filled = fillBlanks(profile, suggestions, [...STEP_FIELDS.firm, ...STEP_FIELDS.address])
    setDraft(filled.draft)
    setPrefilled(filled.filled)
    setSettings(data.settings ?? {})
    setViewing(data.is_onboarded ? 'done' : resumeStep(data.step))
  }, [accept])

  useEffect(() => {
    void load().catch(() => {
      setLoadFailed(true)
      setError('We couldn’t load your setup just now.')
    })
  }, [load])

  // The template's name for *Done*, when there is one to name.
  useEffect(() => {
    const code = saved.website_template
    if (!code || viewing !== 'done') return
    site
      .templates()
      .then((r) => setTemplateName(r.data.templates.find((t) => t.code === code)?.name ?? null))
      .catch(() => undefined)
  }, [saved.website_template, viewing])

  const furthest: OnboardingStep = read
    ? read.is_onboarded
      ? 'done'
      : resumeStep(read.step)
    : 'profession'
  const optional = read?.optional ?? FALLBACK_OPTIONAL
  const isOptional = optional.includes(viewing)

  const set = (field: keyof FirmProfile, value: unknown) =>
    setDraft((prev) => ({ ...prev, [field]: value }))
  const setSetting = <K extends keyof FirmSettings>(field: K, value: FirmSettings[K]) =>
    setSettings((prev) => ({ ...prev, [field]: value }))

  /** What stops *Continue* before any request, or `null`. */
  const problem = useMemo(() => {
    const gaps = stepGaps(viewing, draft)
    if (gaps.length) {
      return viewing === 'firm' ? `Still needed: ${gaps.join(' and ')}.` : `${gaps[0]}.`
    }
    if (viewing === 'firm') return firmStepProblem(draft)
    if (viewing === 'address')
      return addressStepProblem(draft, saved) ?? hoursProblem(draft.working_hours ?? null)
    if (viewing === 'billing') return billingStepProblem(settings)
    return null
  }, [viewing, draft, saved, settings])

  const open = (step: OnboardingStep) => {
    setError(null)
    setViewing(step)
  }

  /** Land after an accepted step: the next one, unless the service says an
   *  earlier required answer is missing. */
  const landAfter = (step: OnboardingStep, body: OnboardingRead) => {
    const next = nextStep(step)
    const server = body.is_onboarded ? 'done' : resumeStep(body.step)
    const land =
      ONBOARDING_STEPS.indexOf(server) < ONBOARDING_STEPS.indexOf(next) ? server : next
    setViewing(land === 'done' && !body.is_onboarded ? 'domain' : land)
  }

  const submit = async (step: OnboardingStep, { skip = false } = {}) => {
    setSaving(true)
    setError(null)
    try {
      const fields = STEP_FIELDS[step]
      const changes = skip ? {} : changedFields(saved, draft, fields)
      const switches: Partial<FirmSettings> = {}
      if (step === 'billing' && !skip) {
        for (const field of BILLING_FIELDS) {
          if (settings[field] !== savedSettings[field]) {
            ;(switches as Record<string, unknown>)[field] = settings[field]
          }
        }
      }
      const body = (await firm.advance(step, changes, switches)).data as OnboardingRead
      accept(body)
      const profile = body.profile ?? {}
      // This step's fields take the server's answer; every other step's
      // unsaved edits (and the organisation's prefill) are kept.
      setDraft((prev) => ({ ...prev, ...pick(profile, fields) }))
      if (step === 'billing') setSettings(body.settings ?? settings)
      setPrefilled((prev) => prev.filter((field) => !fields.includes(field)))
      landAfter(step, body)
    } catch (err) {
      // The service names the fields it still wants; showing those beats
      // showing "invalid".
      setError(refusalDetail(err) ?? 'That step could not be saved.')
    } finally {
      setSaving(false)
    }
  }

  /** *Finish setup* and *Skip for now* on the Domain step are the same
   *  request: the card has already adopted whatever there was to adopt. */
  const finish = async () => {
    setSaving(true)
    setError(null)
    try {
      const body = (await firm.complete()).data as OnboardingRead
      accept(body)
      setViewing('done')
    } catch (err) {
      setError(
        err instanceof ApiError
          ? (refusalDetail(err) ?? err.message)
          : 'Setup could not be completed.',
      )
    } finally {
      setSaving(false)
    }
  }

  if (!can(SETTINGS, 'can_view')) return null

  const heading = HEADINGS[viewing]
  const back = previousStep(viewing)
  const firstName = draft.display_name || saved.legal_name

  return (
    <div className="mx-auto w-full max-w-6xl">
      <div className="overflow-hidden rounded-3xl border border-border bg-card shadow-sm lg:grid lg:min-h-[640px] lg:grid-cols-[300px_minmax(0,1fr)]">
        {/* -- the left panel: a welcome and the stepper ------------------- */}
        <aside className="hidden border-r border-border bg-primary/5 p-8 lg:flex lg:flex-col lg:gap-8">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-primary">
              Welcome to ProDesk
            </p>
            <h1 className="mt-2 text-xl font-semibold text-foreground">
              {firstName ? `Let’s set up ${firstName}` : 'Let’s set up your practice'}
            </h1>
            <p className="mt-2 text-sm text-muted-foreground">
              About five minutes. Everything saves as you go — stop at any point and you’ll come
              back to where you were.
            </p>
          </div>
          <Stepper viewing={viewing} furthest={furthest} onOpen={open} />
        </aside>

        {/* -- the right panel: the step ----------------------------------- */}
        <div className="flex min-w-0 flex-col">
          <div className="border-b border-border bg-primary/5 p-5 lg:hidden">
            <p className="text-xs font-semibold uppercase tracking-wider text-primary">
              Set up your practice
            </p>
            <div className="mt-3">
              <StepProgress viewing={viewing} />
            </div>
          </div>

          <main className="flex-1 space-y-6 p-5 sm:p-8" aria-labelledby="step-title">
            {viewing !== 'done' && (
              <header>
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  {STEP_LABELS[viewing]}
                  {isOptional ? ' · optional' : ''}
                </p>
                <h2 id="step-title" className="mt-1 text-2xl font-semibold text-foreground">
                  {heading.title}
                </h2>
                <p className="mt-1 max-w-2xl text-sm text-muted-foreground">{heading.description}</p>
              </header>
            )}

            {error && (
              <Alert tone="error">
                {error}
                {loadFailed && (
                  <>
                    {' '}
                    <Button variant="link" onClick={() => void load().catch(() => undefined)}>
                      Try again
                    </Button>
                  </>
                )}
              </Alert>
            )}

            {!editable && read && (
              <Alert tone="info">
                You can see the firm’s setup here. Answering it is for a partner or your practice
                manager.
              </Alert>
            )}

            {viewing !== 'done' && prefilled.some((f) => STEP_FIELDS[viewing].includes(f)) && (
              <Alert tone="info">
                We’ve filled in the blanks we could from your organisation’s profile on the
                platform. Check them — nothing is saved until you continue.
              </Alert>
            )}

            {!read && !loadFailed ? (
              <div className="space-y-4" aria-busy="true">
                <Skeleton className="h-8 w-2/3 rounded-lg" />
                <Skeleton className="h-48 w-full rounded-2xl" />
              </div>
            ) : (
              read && (
                <>
                  {viewing === 'profession' && (
                    <ProfessionCards
                      value={(draft.profession ?? null) as Profession | null}
                      disabled={!editable || saving}
                      onChange={(next) => set('profession', next)}
                      // w13b — ADR-0031: *"What do you do?"* with `other`.
                      otherLabel={draft.profession_label ?? ''}
                      onOtherLabelChange={(next) => set('profession_label', next)}
                    />
                  )}
                  {viewing === 'firm' && (
                    <FirmStep draft={draft} set={set} disabled={!editable} prefilled={prefilled} />
                  )}
                  {viewing === 'address' && (
                    <AddressStep
                      draft={draft}
                      saved={saved}
                      set={set}
                      disabled={!editable}
                      prefilled={prefilled}
                    />
                  )}
                  {viewing === 'billing' && (
                    <BillingStep
                      settings={settings}
                      setSetting={setSetting}
                      firmState={saved.state ?? null}
                      disabled={!editable}
                    />
                  )}
                  {viewing === 'website' && (
                    <WebsiteStep
                      value={draft.website_template ?? null}
                      profession={saved.profession ?? draft.profession ?? null}
                      disabled={!editable || saving}
                      onChange={(code) => set('website_template', code)}
                    />
                  )}
                  {viewing === 'domain' && (
                    // C27 (UAT report #7): Website → Domain's own card — core's
                    // bound hosts, adopted or picked, and nothing typed.
                    <div className="space-y-4" data-onboarding-domain="">
                      <DomainStep editable={editable} heading={false} />
                    </div>
                  )}
                  {viewing === 'done' && <DoneStep profile={saved} templateName={templateName} />}
                </>
              )
            )}
          </main>

          {/* -- the step's actions: back, skip, one primary ---------------- */}
          {read && viewing !== 'done' && editable && (
            <footer className="sticky bottom-0 z-10 flex flex-col-reverse gap-3 border-t border-border bg-card/95 p-4 backdrop-blur sm:flex-row sm:items-center sm:justify-between sm:px-8">
              <div>
                {back && canOpen(back, furthest) && (
                  <Button variant="ghost" onClick={() => open(back)} disabled={saving}>
                    <ArrowLeft size={16} aria-hidden /> Back
                  </Button>
                )}
              </div>
              <div className="flex flex-col-reverse items-stretch gap-2 sm:flex-row sm:items-center">
                {problem && (
                  <p role="status" className="text-sm text-muted-foreground sm:mr-2">
                    {problem}
                  </p>
                )}
                {isOptional && (
                  <Button
                    variant="outline"
                    disabled={saving}
                    onClick={() => {
                      if (viewing === 'domain') {
                        void finish()
                      } else {
                        setDraft((prev) => ({ ...prev, ...pick(saved, STEP_FIELDS[viewing]) }))
                        if (viewing === 'billing') setSettings(savedSettings)
                        void submit(viewing, { skip: true })
                      }
                    }}
                  >
                    Skip for now
                  </Button>
                )}
                {viewing === 'domain' ? (
                  <Button
                    onClick={() => void finish()}
                    loading={saving}
                    loadingText="Finishing…"
                  >
                    Finish setup <ArrowRight size={16} aria-hidden />
                  </Button>
                ) : (
                  <Button
                    onClick={() => void submit(viewing)}
                    loading={saving}
                    loadingText="Saving…"
                    disabled={Boolean(problem)}
                  >
                    Continue <ArrowRight size={16} aria-hidden />
                  </Button>
                )}
              </div>
            </footer>
          )}
        </div>
      </div>

      {viewing === 'profession' && draft.profession && (
        <p className="mt-4 text-center text-xs text-muted-foreground">
          {professionName(draft.profession, draft.profession_label)} — you can change this later in Settings →
          Profession.
        </p>
      )}
    </div>
  )
}
